import { FC, ReactElement, memo, useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { AppMessage } from '@/common/components';
import { AppRoutes } from '@/common/interfaces';
import { FallbackedLayout } from '@/common/layouts';
import { messageOnError } from '@/common/utils';

import {
    ExternalRedirectsQueryKeys,
    useBulkUpdateActivityExternalRedirectsMutation,
    useDeleteExternalRedirectsMutation,
    useGetOneByIdExternalRedirectsQuery,
} from '@/modules/ExternalRedirects';

import { ExternalRedirectsOverviewView } from './ExternalRedirectsOverviewView';

interface Props {
  siteId: string;
  externalRedirectId: string;
}

export const ExternalRedirectsOverviewContainer: FC<Props> = memo(({ siteId, externalRedirectId }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const appMessageApi = AppMessage.useControls();

  const {
    data: externalRedirectData,
    error: externalRedirectError,
    isLoading: isExternalRedirectLoading,
    isFetching: isExternalRedirectFetching,
    isError: isExternalRedirectError,
    refetch: refetchExternalRedirect
  } = useGetOneByIdExternalRedirectsQuery(siteId, externalRedirectId)({ enabled: !!externalRedirectId });

  const { isLoading: isDeleteExternalRedirectLoading, mutate: deleteExternalRedirectMutation } =
  useDeleteExternalRedirectsMutation(siteId, externalRedirectId)({
    onSuccess: async () => {
      appMessageApi.success('Зовнішній редірект успішно видалено!');

      navigate(AppRoutes.ExternalRedirects.replace(':siteId', siteId));

      await Promise.all([
        queryClient.refetchQueries([ExternalRedirectsQueryKeys.GetAll]),
        queryClient.refetchQueries([ExternalRedirectsQueryKeys.Conflicts]),
      ]);
    },
    onError: (error) => {
      messageOnError(error, appMessageApi);
    }
  });

  const { isLoading: isActivityExternalRedirectLoading, mutate: activityExternalRedirectMutation } =
  useBulkUpdateActivityExternalRedirectsMutation(siteId)({
    onSuccess: async () => {
      await refetchExternalRedirect();

      await Promise.all([
        queryClient.refetchQueries([ExternalRedirectsQueryKeys.GetAll]),
        queryClient.refetchQueries([ExternalRedirectsQueryKeys.Conflicts]),
      ]);
    },
    onError: (error) => {
      messageOnError(error, appMessageApi);
    }
  });

  const handleDeleteExternalRedirect = useCallback((): void => {
    deleteExternalRedirectMutation();
  }, []);

  const handleEditExternalRedirect = useCallback((): void => {
    navigate(AppRoutes.ExternalRedirectsEdit.replace(':siteId', siteId).replace(':externalRedirectId', externalRedirectId));
  }, [siteId, externalRedirectId]);

  const handleActivateExternalRedirect = useCallback((): void => {
    if (!externalRedirectData) return;

    activityExternalRedirectMutation({ ids: [externalRedirectId], isActive: !externalRedirectData.isActive });
  }, [externalRedirectId, externalRedirectData]);

  return (
    <FallbackedLayout
      isLoading={ isExternalRedirectLoading }
      isError={ isExternalRedirectError }
      error={ externalRedirectError }
      data={ externalRedirectData }
    >
      {
        (externalRedirect): ReactElement => (
          <ExternalRedirectsOverviewView
            externalRedirect={ externalRedirect }
            disableButtons={ isExternalRedirectFetching || isExternalRedirectLoading }
            isDeleteExternalRedirectLoading={ isDeleteExternalRedirectLoading }
            isActivityExternalRedirectLoading={ isActivityExternalRedirectLoading }
            handleEditExternalRedirect={ handleEditExternalRedirect }
            handleDeleteExternalRedirect={ handleDeleteExternalRedirect }
            handleActivateExternalRedirect={ handleActivateExternalRedirect }
          />
        )
      }
    </FallbackedLayout>
  );
});
